"use client";

import Link from "next/link";
import Script from "next/script";
import React, { useState } from "react";

interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare let grecaptcha: Grecaptcha;

const STARS = [1, 2, 3, 4, 5];

interface ReviewSubmissionFormProps {
  /** Called after successful submit (e.g. to close a modal) */
  onSuccess?: () => void;
}

export default function ReviewSubmissionForm({
  onSuccess,
}: ReviewSubmissionFormProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    title: "",
    review: "",
    stars: 5,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const numValue = name === "stars" ? Number(value) : value;
    setFormData((prev) => ({ ...prev, [name]: numValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    if (typeof grecaptcha === "undefined") {
      setErrorMessage("Security check not ready. Please try again.");
      setLoading(false);
      return;
    }

    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(SITE_KEY, { action: "submit" });
        const response = await fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData,
            token,
            formType: "review",
            sendVisitorEmail: true,
          }),
        });

        if (response.ok) {
          setSuccessMessage("Thank you! Your review has been submitted.");
          setSubmitted(true);
          onSuccess?.();
        } else {
          const data = await response.json();
          setErrorMessage(
            data.message ?? "Something went wrong. Please try again."
          );
        }
      } catch {
        setErrorMessage("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    });
  };

  if (submitted) {
    return (
      <div className="rounded-2xl border border-violet-200 bg-violet-50 p-6 text-center">
        <p className="text-lg font-medium text-violet-900">{successMessage}</p>
        <p className="mt-2 text-sm text-violet-700">
          We will review and publish it on our website soon.
        </p>
      </div>
    );
  }

  return (
    <section className="w-full" aria-label="Submit your review">
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`}
        strategy="lazyOnload"
      />
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="review-name"
              className="mb-1 block text-sm font-medium text-violet-950"
            >
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="review-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Your name"
              className="w-full rounded-lg border border-violet-200 px-3 py-2 text-violet-950 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              aria-required
            />
          </div>
          <div>
            <label
              htmlFor="review-email"
              className="mb-1 block text-sm font-medium text-violet-950"
            >
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="review-email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your@email.com"
              className="w-full rounded-lg border border-violet-200 px-3 py-2 text-violet-950 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              aria-required
            />
          </div>
        </div>
        <div>
          <label
            htmlFor="review-title"
            className="mb-1 block text-sm font-medium text-violet-950"
          >
            Title
          </label>
          <input
            id="review-title"
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Short headline for your review"
            maxLength={120}
            className="w-full rounded-lg border border-violet-200 px-3 py-2 text-violet-950 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
          />
        </div>
        <div>
          <label
            htmlFor="review-stars"
            className="mb-1 block text-sm font-medium text-violet-950"
          >
            Rating <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2" role="group" aria-label="Star rating">
            {STARS.map((n) => (
              <label key={n} className="cursor-pointer">
                <input
                  type="radio"
                  name="stars"
                  value={n}
                  checked={formData.stars === n}
                  onChange={handleChange}
                  className="sr-only"
                />
                <span
                  className={`inline-block text-2xl ${
                    formData.stars >= n ? "text-amber-400" : "text-gray-300"
                  }`}
                  aria-hidden
                >
                  ★
                </span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label
            htmlFor="review-text"
            className="mb-1 block text-sm font-medium text-violet-950"
          >
            Your review <span className="text-red-500">*</span>
          </label>
          <textarea
            id="review-text"
            name="review"
            value={formData.review}
            onChange={handleChange}
            required
            rows={5}
            placeholder="Share your experience..."
            className="w-full rounded-lg border border-violet-200 px-3 py-2 text-violet-950 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
            aria-required
          />
        </div>
        {errorMessage && (
          <p className="text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="submit"
            disabled={loading}
            className="rounded-lg bg-violet-600 px-6 py-2.5 font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Sending…" : "Submit review"}
          </button>
          <p className="text-xs text-violet-700">
            By submitting you agree to our{" "}
            <Link href="/" className="text-violet-600 underline">
              Privacy Policy
            </Link>
            . This site is protected by reCAPTCHA.
          </p>
        </div>
      </form>
    </section>
  );
}
